import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Title, BarElement } from 'chart.js';
import { Box, CircularProgress, Alert } from '@mui/material';
import { api, getContentText } from './api';
import { AuthProvider, useAuth } from './context/AuthContext';
import Sidebar from './components/Sidebar';
import ProtectedRoute from './components/ProtectedRoute';
import ReplyDialog from './components/ReplyDialog';

// Import all the page components
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import KeywordsPage from './pages/KeywordsPage';
import MentionsPage from './pages/MentionsPage';
import SentimentPage from './pages/SentimentPage';
import TrendsPage from './pages/TrendsPage';
import NewsPage from './pages/NewsPage';
import FacebookMonitoringPage from './pages/FacebookMonitoringPage';
import RawDataViewPage from './pages/RawDataViewPage';
import AlertsPage from './pages/AlertsPage';
import SimulationPage from './pages/SimulationPage';
import ConfigurationPage from './pages/ConfigurationPage';
import PermissionsPage from './pages/PermissionsPage';
import InsightsPage from './pages/InsightsPage';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Title, BarElement);

// --- Permission Context and Components ---
const PermissionContext = createContext(null);

const PermissionProvider = ({ children }) => {
    const { user } = useAuth();
    const hasAccess = (pageName) => {
        if (!user) return false;
        if (user.role === 'admin') return true;
        const permissions = user.UserPermissions || [];
        const permission = permissions.find(p => p.page === pageName);
        return permission ? permission.hasAccess : true;
    };
    return ( <PermissionContext.Provider value={{ hasAccess }}> {children} </PermissionContext.Provider> );
};

export const usePermissions = () => useContext(PermissionContext);

const PermissionRoute = ({ pageName, children }) => {
    const { hasAccess } = usePermissions();
    if (hasAccess(pageName)) { return children; }
    return <Navigate to="/dashboard" replace />;
};

export default function App() {
    return ( <Router> <AuthProvider> <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/*" element={<ProtectedRoute><MainLayout /></ProtectedRoute>} />
    </Routes> </AuthProvider> </Router> );
}

const MainLayout = () => {
    const [state, setState] = useState({
        keywords: [], mentions: [], config: {}, alerts: [], triggeredAlerts: [],
        trends: { interestOverTime: { labels: [], datasets: [] }, relatedKeywords: [] },
        users: []
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { token, user } = useAuth();
    
    const [replyDialogOpen, setReplyDialogOpen] = useState(false);
    const [replyContent, setReplyContent] = useState('');
    const [isReplyLoading, setIsReplyLoading] = useState(false);
    // --- THIS IS THE FIX (Part 1) ---
    // The dependency array for useCallback is now minimal and correct.
    // This stops the infinite loop.
    const initializeApp = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        setError(null);
        try {
            const promises = [
                api.get(`/keywords`), api.get(`/mentions`), api.get(`/configurations`),
                api.get(`/alerts`), api.get(`/alerts/triggered`),
            ];
            if (user?.role === 'admin') { promises.push(api.get(`/users`)); }
            
            const results = await Promise.allSettled(promises);

            // Helper to safely extract data or return an empty array/object
            const getData = (result, defaultType = []) => 
                result.status === 'fulfilled' ? result.value.data : defaultType;
            
            const trendsDefault = { interestOverTime: { labels: [], datasets: [] }, relatedKeywords: [] };

            const keywords = getData(results[0]);
            const mentions = getData(results[1]);
            const config = getData(results[2], {});
            const alerts = getData(results[3]);

            const triggeredAlerts = getData(results[4]);
            const users = user?.role === 'admin' && results[5] ? getData(results[5]) : [];
            
            setState({ keywords, mentions, config, alerts, triggeredAlerts, users });
        } catch (err) {
            console.error("Initialization Error:", err);
            setError("Failed to connect to the backend. Please ensure the server is running.");
        } finally {
            setLoading(false);
        }
    }, [token, user?.role]);

    // --- THIS IS THE FIX ---
    // This useEffect hook is now explicitly dependent on the `token`.
    // It will only run the `initializeApp` function AFTER the token has been
    // successfully set in the AuthProvider. This eliminates the race condition.
    useEffect(() => {
        if (token) {
            initializeApp();
        }
    }, [token, initializeApp]);

    const handleGenerateReply = async (mention) => {
        setReplyContent('');
        setReplyDialogOpen(true);
        setIsReplyLoading(true);
        try {
            const response = await api.post('/utility/generate-reply', { 
                content: getContentText(mention.content), 
                sentiment: mention.sentiment 
            });
            setReplyContent(response.data.reply);
        } catch (error) {
            setReplyContent('Sorry, could not generate a reply.');
        } finally {
            setIsReplyLoading(false);
        }
    };

    if (loading && !state.mentions.length) { // Show loading only on the very first load
        return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>;
    }

    return (
        <PermissionProvider>
            <Box sx={{ display: 'flex' }}>
                <Sidebar />
                <Box component="main" sx={{ flexGrow: 1, p: 3, height: '100vh', overflow: 'auto', bgcolor: '#f3f4f6' }}>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    <Routes>
                        <Route path="/dashboard" element={<PermissionRoute pageName="Dashboard"><DashboardPage state={state} onGenerateReply={handleGenerateReply} refreshData={initializeApp}/></PermissionRoute>} />
                        <Route path="/insights" element={<PermissionRoute pageName="Insights"><InsightsPage state={state} /></PermissionRoute>} />
                        <Route path="/keywords" element={<PermissionRoute pageName="Keywords"><KeywordsPage state={state} refreshData={initializeApp} /></PermissionRoute>} />
                        <Route path="/mentions" element={<PermissionRoute pageName="Mentions"><MentionsPage state={state} onGenerateReply={handleGenerateReply} /></PermissionRoute>} />
                        <Route path="/sentiment" element={<PermissionRoute pageName="Sentiment"><SentimentPage state={state} refreshData={initializeApp} onGenerateReply={handleGenerateReply} /></PermissionRoute>} />
                        <Route path="/trends" element={<PermissionRoute pageName="Trends"><TrendsPage state={state} /></PermissionRoute>} />
                        <Route path="/news" element={<PermissionRoute pageName="News"><NewsPage state={state} refreshData={initializeApp} /></PermissionRoute>} />
                        <Route path="/facebook-monitoring" element={<PermissionRoute pageName="Facebook Monitoring"><FacebookMonitoringPage refreshData={initializeApp} /></PermissionRoute>} />
                        <Route path="/raw-data-view" element={<PermissionRoute pageName="Raw Data View"><RawDataViewPage /></PermissionRoute>} />
                        <Route path="/alerts" element={<PermissionRoute pageName="Alerts"><AlertsPage state={state} refreshData={initializeApp} onGenerateReply={handleGenerateReply} /></PermissionRoute>} />
                        <Route path="/simulation" element={<PermissionRoute pageName="Simulation"><SimulationPage state={state} refreshData={initializeApp} /></PermissionRoute>} />
                        <Route path="/configuration" element={<PermissionRoute pageName="Configuration"><ConfigurationPage state={state} config={state.config} refreshData={initializeApp} /></PermissionRoute>} />
                        <Route path="/permissions" element={<PermissionRoute pageName="Permissions"><PermissionsPage state={state} users={state.users} refreshData={initializeApp} /></PermissionRoute>} />
                        <Route path="*" element={<Navigate to="/dashboard" />} />
                    </Routes>
                </Box>
                 <ReplyDialog 
                    open={replyDialogOpen} 
                    onClose={() => setReplyDialogOpen(false)} 
                    content={replyContent} 
                    isLoading={isReplyLoading} 
                />
            </Box>
        </PermissionProvider>
    );
};

